import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {InputText} from 'primereact/inputtext'
import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import PhoneInput from 'react-phone-input-2'
import TranslateUtils from '../../i18n/TranslateUtils'
import {useAuth} from '../authentication/AuthProvider'
import DropdownUtils from '../form-utils/DropdownUtils'
import {component} from '../form/Constant'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import {addressTypeOptions, addressTypes} from './AddressConstant'
import {getListGenericAddress} from './AddressEndPoint'

const PATH = 'address'

const CountryPath = '/getCountries'
const StatePath = '/getStates'
const CityPath = '/getCities'

const CountryQueryKey = 'Countries-AddressForm'
const StateQueryKey = 'States-AddressForm'
const CityQueryKey = 'Cities-AddressForm'

const AddressForm = forwardRef(
    (
        {
            id,
            filter = false,
            gpForm = false,
            disabled = false,
            className = '',
            childrenClassName = '',
            defaultValue = {},
            customDetail = false,
            disableList = [],
            callBack = () => {},
        },
        ref,
    ) => {
        useSignals()
        useImperativeHandle(ref, () => ({
            getValue: () => handleGetValue(),
        }))

        const {user} = useAuth()
        const addressRefs = useRef([])
        const queryClient = useQueryClient()
        const {
            translateKeys,
            translateAddressOptions,
            translateOptions,
            translateCommons,
        } = TranslateUtils()

        const defaultValueFilled = useMemo(() => {
            if (!!defaultValue) {
                if (
                    defaultValue?.addressConfig &&
                    defaultValue.addressConfig?.inputFields &&
                    defaultValue.addressConfig.inputFields?.length > 0 &&
                    defaultValue.addressConfig.inputFields?.includes('refState')
                ) {
                    return {
                        ...defaultValue,
                        refState: defaultValue?.state
                            ? defaultValue?.state
                            : null,
                    }
                } else {
                    return {
                        ...defaultValue,
                        refState: defaultValue?.refState
                            ? defaultValue?.refState
                            : null,
                    }
                }
            } else return {}
        }, [defaultValue])

        const refCountry = useSignal(
            defaultValueFilled && defaultValueFilled['refCountry']
                ? defaultValueFilled['refCountry']
                : '',
        )
        const refState = useSignal(
            defaultValueFilled && defaultValueFilled['refState']
                ? defaultValueFilled['refState']
                : '',
        )

        const getAddressData = async (path, userId) => {
            return await await getListGenericAddress(
                path,
                userId,
                refCountry.value,
                refState.value,
            )
        }

        const initInputKeys = useMemo(() => {
            let holderKeys = []
            if (
                defaultValueFilled?.addressConfig &&
                defaultValueFilled.addressConfig?.inputFields?.length > 0
            ) {
                if (
                    defaultValueFilled.addressConfig?.inputFields?.includes(
                        'refState',
                    )
                ) {
                    holderKeys.push('refState')
                }
                if (
                    defaultValueFilled.addressConfig?.inputFields?.includes(
                        'city',
                    )
                ) {
                    holderKeys.push('city')
                }
            }
            return holderKeys
        }, [defaultValueFilled])

        const switchInputKeys = useSignal([...initInputKeys])

        const {data: countryOptions} = useQuery(
            [CountryQueryKey],
            async () => await getAddressData(PATH + CountryPath, user?.id),
            {
                refetchOnMount: false,
            },
        )

        const {data: stateOptions} = useQuery(
            [StateQueryKey, refCountry.value, switchInputKeys.value],
            async () => await getAddressData(PATH + StatePath, user?.id),
            {
                enabled:
                    !!refCountry.value &&
                    !switchInputKeys.value.includes('refState'),
                refetchOnMount: false,
            },
        )
        const {mutate: clearCityOptions} = useMutation(() => {}, {
            onSuccess: () => {
                queryClient.setQueryData(
                    [
                        CityQueryKey,
                        refCountry.value,
                        refState.value,
                        switchInputKeys.value,
                    ],
                    () => [],
                )
            },
        })

        const handleSwitchInput = useCallback(
            key => {
                let callBackData = {}
                if (switchInputKeys.value?.includes(key)) {
                    switchInputKeys.value = switchInputKeys.value.filter(
                        x => x !== key,
                    )

                    if (key === 'refState') {
                        refState.value = null

                        callBackData = {
                            refCountry: refCountry.value || null,
                            refState: null,
                            city: null,
                        }

                        clearCityOptions()
                    } else if (key === 'city') {
                        callBackData = {
                            refCountry: refCountry.value || null,
                            refState: refState.value || null,
                            city: null,
                        }
                    }
                } else {
                    if (
                        key === 'refState' &&
                        !switchInputKeys.value.includes('city')
                    ) {
                        switchInputKeys.value = [
                            ...switchInputKeys.value,
                            key,
                            'city',
                        ]

                        callBackData = {
                            refCountry: refCountry.value || null,
                            refState: null,
                            city: null,
                        }
                    } else if (key === 'city') {
                        switchInputKeys.value = [...switchInputKeys.value, key]

                        callBackData = {
                            refCountry: refCountry.value || null,
                            refState: refState.value || null,
                            city: null,
                        }
                    } else {
                        switchInputKeys.value = [...switchInputKeys.value, key]
                    }
                }

                clearCityOptions()

                if (
                    callBack &&
                    Object.keys(callBackData) &&
                    Object.keys(callBackData)?.length > 0
                ) {
                    callBack({...callBackData})
                }
            },
            [switchInputKeys, clearCityOptions, callBack, refCountry, refState],
        )

        const handleOnChange = useCallback(
            (data, type = 'refCountry') => {
                let callBackData = {}
                if (type === 'refCountry') {
                    refCountry.value = data
                    refState.value = null
                    clearCityOptions()

                    callBackData = {
                        refCountry: data || null,
                        refState: null,
                        city: null,
                    }
                }

                if (type === 'refState') {
                    refState.value = data

                    callBackData = {
                        refCountry: refCountry.value || null,
                        refState: data || null,
                        city: null,
                    }
                }

                if (type === 'city') {
                    callBackData = {
                        refCountry: refCountry.value || null,
                        refState: refState.value || null,
                        city: data || null,
                    }
                }

                if (callBack) {
                    callBack(callBackData)
                }
            },
            [callBack, clearCityOptions, refCountry, refState],
        )

        const {data: cityOptions} = useQuery(
            [
                CityQueryKey,
                refCountry.value,
                refState.value,
                switchInputKeys.value,
            ],
            async () => await getAddressData(PATH + CityPath, user?.id),
            {
                enabled:
                    !!refCountry.value &&
                    !!refState.value &&
                    !switchInputKeys.value.includes('city') &&
                    !switchInputKeys.value.includes('refState'),
                refetchOnMount: false,
            },
        )

        const addressList = useMemo(() => {
            return [
                {
                    key: 'type',
                    label: 'Address Type',
                    placeholder: translateKeys('Optional'),
                    component: component.dropdown,
                    options: translateOptions(addressTypeOptions),
                    defaultValue: addressTypes.UNKNOWN.value,
                },
                {
                    key: 'refCountry',
                    label: 'Country',
                    placeholder: translateKeys('Optional'),
                    component: component.dropdown,
                    options: translateAddressOptions(countryOptions, 'country'),
                },
                {
                    key: 'refState',
                    label: 'State',
                    placeholder: translateKeys('Optional'),
                    component: component.dropdown,
                    allowInput: true,
                    options: translateAddressOptions(stateOptions, 'state'),
                },
                {
                    key: 'city',
                    label: 'City',
                    placeholder: translateKeys('Optional'),
                    component: component.dropdown,
                    allowInput: true,
                    options: translateAddressOptions(cityOptions, 'city'),
                },
                {
                    key: 'addressLine1',
                    label: 'Address Line 1',
                    placeholder: translateKeys('Optional'),
                    component: component.inputText,
                },
                {
                    key: 'addressLine2',
                    label: 'Address Line 2',
                    placeholder: translateKeys('Optional'),
                    component: component.inputText,
                },
                {
                    key: 'addressLine3',
                    label: 'Address Line 3',
                    placeholder: translateKeys('Optional'),
                    component: component.inputText,
                },
                {
                    key: 'addressLine4',
                    label: 'Address Line 4',
                    placeholder: translateKeys('Optional'),
                    component: component.inputText,
                },
                {
                    key: 'postalCode',
                    label: 'Postal Code',
                    placeholder: translateKeys('Optional'),
                    component: component.inputText,
                },
                {
                    key: 'phone',
                    label: 'Phone',
                    component: component.phoneInput,
                },
            ]
        }, [
            translateKeys,
            translateOptions,
            translateAddressOptions,
            countryOptions,
            stateOptions,
            cityOptions,
        ])

        const addressFilterList = useMemo(() => {
            return [
                {
                    key: 'refCountry',
                    label: 'Country',
                    placeholder: translateCommons('Select'),
                    component: component.dropdown,
                    options: translateAddressOptions(countryOptions, 'country'),
                },
                {
                    key: 'refState',
                    label: 'State',
                    placeholder: translateCommons('Select'),
                    component: component.dropdown,
                    allowInput: true,
                    options: translateAddressOptions(stateOptions, 'state'),
                },
                {
                    key: 'city',
                    label: 'City',
                    placeholder: translateCommons('Select'),
                    component: component.dropdown,
                    allowInput: true,
                    options: translateAddressOptions(cityOptions, 'city'),
                },
            ]
        }, [
            translateCommons,
            translateAddressOptions,
            countryOptions,
            stateOptions,
            cityOptions,
        ])
        const addressGpList = useMemo(() => {
            return [
                {
                    key: 'type',
                    label: 'Address Type',
                    placeholder: translateKeys('Optional'),
                    component: component.dropdown,
                    options: translateOptions(addressTypeOptions),
                    defaultValue: addressTypes.UNKNOWN.value,
                },
                {
                    key: 'refCountry',
                    label: 'Country',
                    placeholder: translateKeys('Optional'),
                    component: component.dropdown,
                    options: translateAddressOptions(countryOptions, 'country'),
                },
                {
                    key: 'refState',
                    label: 'State',
                    placeholder: translateKeys('Optional'),
                    component: component.dropdown,
                    allowInput: true,
                    options: translateAddressOptions(stateOptions, 'state'),
                },
                {
                    key: 'city',
                    label: 'City',
                    placeholder: translateKeys('Optional'),
                    component: component.dropdown,
                    allowInput: true,
                    options: translateAddressOptions(cityOptions, 'city'),
                },
                {
                    key: 'addressLine1',
                    label: 'Address Line 1',
                    placeholder: translateKeys('Optional'),
                    component: component.inputText,
                },
                {
                    key: 'addressLine2',
                    label: 'Address Line 2',
                    placeholder: translateKeys('Optional'),
                    component: component.inputText,
                },
                {
                    key: 'postalCode',
                    label: 'Postal Code',
                    placeholder: translateKeys('Optional'),
                    component: component.inputText,
                },
                {
                    key: 'phone',
                    label: 'Phone',
                    component: component.phoneInput,
                },
            ]
        }, [
            translateKeys,
            translateOptions,
            translateAddressOptions,
            countryOptions,
            stateOptions,
            cityOptions,
        ])

        const dataList = useMemo(() => {
            return filter
                ? addressFilterList
                : gpForm
                ? addressGpList
                : addressList
        }, [addressFilterList, addressList, filter, gpForm, addressGpList])

        const handleGetValue = useCallback(() => {
            let data = {}
            for (let i = 0; i < dataList.length; i++) {
                if (dataList[i].key && !!addressRefs.current[dataList[i].key]) {
                    if (dataList[i].component === component.phoneInput) {
                        data = {
                            ...data,
                            [dataList[i].key]:
                                addressRefs.current[dataList[i].key].state
                                    .formattedNumber,
                            phoneExtension: {
                                dialCode:
                                    addressRefs.current[dataList[i].key].state
                                        .selectedCountry.dialCode,
                                countryCode:
                                    addressRefs.current[dataList[i].key].state
                                        .selectedCountry.iso2,
                            },
                            addressConfig: {
                                inputFields: [...switchInputKeys.value],
                            },
                        }
                    } else {
                        data = {
                            ...data,
                            [dataList[i].key]:
                                addressRefs.current[dataList[i].key].value,
                            addressConfig: {
                                inputFields: [...switchInputKeys.value],
                            },
                        }
                    }
                }
            }
            return data
        }, [dataList, switchInputKeys])

        const renderItem = useCallback(
            itemList => {
                let filterDisableList = itemList.filter(
                    x => !disableList.includes(x.key),
                )
                return filterDisableList?.map(item => (
                    <div
                        key={item.key}
                        className={
                            childrenClassName
                                ? childrenClassName
                                : 'col-12 px-0'
                        }>
                        {!!filter ? (
                            item?.allowInput ? (
                                <div className="flex align-items-center justify-content-between">
                                    <div>
                                        <h6>{translateKeys(item.label)}</h6>
                                    </div>
                                    <div>
                                        <Button
                                            icon={
                                                switchInputKeys.value?.includes(
                                                    item?.key,
                                                )
                                                    ? 'pi pi-list'
                                                    : 'pi pi-pencil'
                                            }
                                            className="p-button-text p-button-plain p-button-rounded"
                                            onClick={() =>
                                                handleSwitchInput(item?.key)
                                            }
                                            tooltip={
                                                switchInputKeys.value?.includes(
                                                    item?.key,
                                                )
                                                    ? translateCommons(
                                                          'Switch to dropdown',
                                                      )
                                                    : translateCommons(
                                                          'Switch to input',
                                                      )
                                            }
                                            tooltipOptions={{
                                                position: 'top',
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <h6>{translateKeys(item.label)}</h6>
                            )
                        ) : !customDetail ? (
                            item?.allowInput ? (
                                <div className="flex align-items-center justify-content-between relative">
                                    <div>
                                        <h5 className="font-bold">
                                            {translateKeys(item.label)}
                                        </h5>
                                    </div>
                                    <div className="">
                                        <Button
                                            icon={
                                                switchInputKeys.value?.includes(
                                                    item?.key,
                                                )
                                                    ? 'pi pi-list'
                                                    : 'pi pi-pencil'
                                            }
                                            className="p-button-text p-button-plain p-button-rounded absolute right-0 "
                                            onClick={() =>
                                                handleSwitchInput(item?.key)
                                            }
                                            style={{top: '-10px'}}
                                            tooltip={
                                                switchInputKeys.value?.includes(
                                                    item?.key,
                                                )
                                                    ? translateCommons(
                                                          'Switch to dropdown',
                                                      )
                                                    : translateCommons(
                                                          'Switch to input',
                                                      )
                                            }
                                            tooltipOptions={{
                                                position: 'top',
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <h5 className="font-bold">
                                    {translateKeys(item.label)}
                                </h5>
                            )
                        ) : (
                            ''
                        )}
                        {item.component === component.dropdown &&
                        !switchInputKeys.value?.includes(item?.key) ? (
                            <div
                                className={`col-12 ${
                                    !customDetail ? 'p-0' : ''
                                } `}>
                                <DropdownUtils
                                    id={item.key}
                                    className={`${
                                        customDetail ? 'custom-detail' : ''
                                    } col-12 p-0 h-3rem`}
                                    optionsList={item.options}
                                    disabled={disabled}
                                    callBack={handleOnChange}
                                    placeholder={translateKeys(
                                        item.placeholder,
                                    )}
                                    inputId={item.label}
                                    customDetail={customDetail ? true : false}
                                    defaultValue={
                                        defaultValueFilled &&
                                        defaultValueFilled[item.key]
                                            ? defaultValueFilled[item.key]
                                            : item.defaultValue
                                            ? item.defaultValue
                                            : null
                                    }
                                    ref={el =>
                                        (addressRefs.current[item.key] = el)
                                    }
                                />
                            </div>
                        ) : item.component === component.inputText ||
                          item.component === component.inputNumber ||
                          switchInputKeys.value?.includes(item?.key) ? (
                            <div
                                className={`col-12 ${
                                    !customDetail ? 'p-0' : ''
                                }`}>
                                <span
                                    className={
                                        customDetail ? 'p-float-label' : ''
                                    }>
                                    <InputText
                                        id={item.key}
                                        className={`${
                                            customDetail ? 'custom-detail' : ''
                                        } col-12 h-3rem `}
                                        disabled={disabled}
                                        placeholder={translateKeys(
                                            item.placeholder,
                                        )}
                                        ref={el =>
                                            (addressRefs.current[item.key] = el)
                                        }
                                        type={
                                            item.component ===
                                            component.inputNumber
                                                ? 'number'
                                                : 'text'
                                        }
                                        defaultValue={
                                            defaultValueFilled &&
                                            defaultValueFilled[item.key]
                                                ? defaultValueFilled[item.key]
                                                : item.defaultValue
                                                ? item.defaultValue
                                                : ''
                                        }
                                    />
                                    {customDetail && (
                                        <label htmlFor={item.key}>
                                            {translateKeys(
                                                convertKeyToLabel(item.key),
                                            )}
                                        </label>
                                    )}
                                </span>
                            </div>
                        ) : (
                            item.component === component.phoneInput && (
                                <PhoneInput
                                    id={item.key}
                                    disabled={disabled}
                                    className={`col-12 ${
                                        customDetail ? 'custom-detail' : 'p-0'
                                    }`}
                                    placeholder={translateKeys(
                                        item.placeholder,
                                    )}
                                    ref={el =>
                                        (addressRefs.current[item.key] = el)
                                    }
                                    enableSearch
                                    country={'gb'}
                                    preferredCountries={[
                                        'gb',
                                        'us',
                                        'ca',
                                        'vn',
                                    ]}
                                    inputStyle={{
                                        minHeight: '39px',
                                    }}
                                    value={
                                        defaultValueFilled &&
                                        defaultValueFilled[item.key]
                                            ? defaultValueFilled[item.key]
                                            : item.defaultValue
                                            ? item.defaultValue
                                            : ''
                                    }
                                />
                            )
                        )}
                    </div>
                ))
            },
            [
                disableList,
                childrenClassName,
                filter,
                translateKeys,
                switchInputKeys,
                translateCommons,
                customDetail,
                disabled,
                handleOnChange,
                defaultValueFilled,
                handleSwitchInput,
            ],
        )

        return (
            <div className={className ? className : 'col-12'} key={id}>
                {renderItem(dataList)}
                {/* {dataList.map((item, idx) => (
                    <div key={idx}>
                        {item?.options?.map((childrenItem, index) => (
                            <div key={index}>{`"${translateKeys(
                                childrenItem.label,
                            )}": "${translateKeys(
                                childrenItem.label,
                            )} +",`}</div>
                        ))}
                    </div>
                ))} */}
            </div>
        )
    },
)

export default AddressForm
